import {createAsyncThunk} from '@reduxjs/toolkit'
import {YandexService} from 'services/yandexSDK'
import APIServiceRiddles from 'services/api/riddles'
import {calcProgress} from 'src/lib/calcProgress'
import type {LangType} from 'src/lib/translates/types'
import {getLang} from 'store/translates/selectors'
import {getRiddleId} from 'store/secretword/selectors'
import {RootState} from '../index'
import {NO_ERROR_MESSAGE} from '../handler'
import {setAnsweredIds, addAnsweredId, setProgress, setCountRiddles} from './slice'
import {getAnsweredIds, getCountRiddles} from './selectors'

const ANSWERED_IDS_KEY = 'gallowonlinegame_answeredids'

export const initAnsweredIdsThunk = createAsyncThunk<void, undefined, { rejectValue: string; state: RootState }>(
  'game/initAnsweredIdsThunk',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState()
      const lang = getLang(state)
      const lcKey = `${ANSWERED_IDS_KEY}_${lang}`
      const answeredIds = JSON.parse(localStorage.getItem(lcKey) ?? '[]')
      thunkAPI.dispatch(setAnsweredIds(answeredIds))
      const count = await APIServiceRiddles.getRiddlesCount({lang})
      thunkAPI.dispatch(setCountRiddles(count))
      const progress = calcProgress(answeredIds.length, count)
      thunkAPI.dispatch(setProgress(progress))
    } catch (error) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue(error.message)
      }
      return thunkAPI.rejectWithValue(NO_ERROR_MESSAGE)
    }
  }
)

export const addAnsweredIdThunk = createAsyncThunk<void, undefined, { rejectValue: string; state: RootState }>(
  'game/saveAnsweredIdsThunk',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState()
      const id = getRiddleId(state)
      if (!id) throw Error('Error: riddle id is null')
      const lang = getLang(state)
      const lcKey = `${ANSWERED_IDS_KEY}_${lang}`
      const currentAnsweredIds = getAnsweredIds(state)
      const isAnswered = currentAnsweredIds.includes(id)
      if (isAnswered) {
        return
      }
      const answeredIds = [...currentAnsweredIds, id]
      localStorage.setItem(lcKey, JSON.stringify(answeredIds))
      thunkAPI.dispatch(addAnsweredId(id))
      const progress = calcProgress(answeredIds.length, getCountRiddles(state))
      thunkAPI.dispatch(setProgress(progress))
      if (YSDK) {
        thunkAPI.dispatch(saveFromLCThunk())
      }
    } catch (error) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue(error.message)
      }
      return thunkAPI.rejectWithValue(NO_ERROR_MESSAGE)
    }
  }
)

export const initLCThunkFromSave =
  createAsyncThunk<void, Record<LangType, Array<string>>, {rejectValue: string; state: RootState}>(
  'game/initLCThunkFromSave',
  async (userData, thunkAPI) => {
    try {
      for(const key in userData) {
        const lcKey = `${ANSWERED_IDS_KEY}_${key}`
        localStorage.setItem(lcKey, JSON.stringify(userData[key as LangType]))
      }
      thunkAPI.dispatch(initAnsweredIdsThunk())
    } catch (error) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue(error.message)
      }
      return thunkAPI.rejectWithValue(NO_ERROR_MESSAGE)
    }
  }
)

export const saveFromLCThunk =
  createAsyncThunk<void, undefined, {rejectValue: string; state: RootState}>(
  'game/saveFromLCThunk',
  async (_, thunkAPI) => {
    try {
      const userData: Record<LangType, Array<string>> = {
        RU: [],
        EN: [],
        TR: [],
        UK: [],
      }
      let length = 0
      for(const key in userData) {
        const lcKey = `${ANSWERED_IDS_KEY}_${key}`
        const answeredIds = JSON.parse(localStorage.getItem(lcKey) ?? '[]')
        length += answeredIds.length
        if (answeredIds.length > 0) {
          userData[key as LangType] = answeredIds
        }
      }

      if(length > 0) {
        await YandexService.saveUserProgress(userData)
      }
    } catch (error) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue(error.message)
      }
      return thunkAPI.rejectWithValue(NO_ERROR_MESSAGE)
    }
  }
)
